// @owners { team: patients }
import { ActionSheetContext, ActionSheetV2, ListBase } from '@alto/design-system';
import {
  AccountActionSheet,
  MedicationsActionSheet,
  MenuOption,
  OrderingActionSheet,
  PricingPaymentsActionSheet,
  TransferActionSheet,
} from '@alto/features';
import { useNavigation } from '@alto/navigation';
import React, { useContext } from 'react';
import { ASSISTANT_PRESSED_EVENTS } from '~shared/features/alto-assistant/analytics/constants';
import {
  ASSISTANT_CHAT_ORIGINS,
  type ActionSheetProviderStackCacheKey,
  type AssistantCategory,
} from '~shared/features/alto-assistant/constants';
import { getPlatformOS } from '~shared/helpers/getPlatformOS';
import { useAnalytics, useIsFeatureDisabledForCompany } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { getRecentShipments } from '~shared/selectors/alto-assistant/getRecentShipments';
import { useSelectorShared } from '~shared/store';

type Props = {
  readonly isActionSheet?: boolean;
  readonly hideDeliveries?: boolean;
};

export const HowCanWeHelpActionSheet = ({ isActionSheet = false, hideDeliveries = false }: Props) => {
  const { trackEvent } = useAnalytics();
  const { navigate } = useNavigation();
  const { closeActionSheet, saveStack, setActiveActionSheet } = useContext(ActionSheetContext);
  const { isFeatureDisabled: shouldDisableTransferPrescription, isLoading: isFeatureDisablingLoading } =
    useIsFeatureDisabledForCompany('transfer_prescription');
  const hasShipments = useSelectorShared(getRecentShipments).length > 0;

  const trackCategoryPress = (category: AssistantCategory) => {
    trackEvent({
      event: EVENTS.ASSISTANT_COMPONENT_PRESSED,
      params: {
        name: ASSISTANT_PRESSED_EVENTS.CATEGORY_CHOSEN,
        category,
      },
    });
  };

  const items = [
    {
      key: 'deliveries',
      component: (
        <MenuOption
          iconName="box"
          text="Deliveries"
          onPress={() => {
            trackCategoryPress('deliveries');
            if (getPlatformOS() !== 'web') {
              // currently, restore stack doesn't work for web, and results in a blank action sheet
              saveStack<ActionSheetProviderStackCacheKey>('assistant-deliveries');
            }
            closeActionSheet();
            navigate('RouteMessagesDeliverySelection', {
              origin: ASSISTANT_CHAT_ORIGINS.ALTO_ASSISTANT_CHOOSE_CATEGORY_MENU,
              category: 'deliveries',
            });
          }}
          hide={!hasShipments || hideDeliveries}
        />
      ),
    },
    {
      key: 'ordering',
      component: (
        <MenuOption
          iconName="cart"
          text="Ordering"
          onPress={() => {
            trackCategoryPress('ordering');
            setActiveActionSheet(<OrderingActionSheet />);
          }}
        />
      ),
    },
    {
      key: 'account',
      component: (
        <MenuOption
          iconName="usercircle"
          text="Account"
          onPress={() => {
            trackCategoryPress('account');
            setActiveActionSheet(<AccountActionSheet />);
          }}
        />
      ),
    },
    {
      key: 'medications',
      component: (
        <MenuOption
          iconName="pillbottle"
          text="Medications"
          onPress={() => {
            trackCategoryPress('medications');
            setActiveActionSheet(<MedicationsActionSheet />);
          }}
        />
      ),
    },
    {
      key: 'pricing-and-payments',
      component: (
        <MenuOption
          iconName="creditcard"
          text="Pricing & payments"
          onPress={() => {
            trackCategoryPress('pricing_payments');
            setActiveActionSheet(<PricingPaymentsActionSheet />);
          }}
        />
      ),
    },
    {
      key: 'transfers',
      component: (
        <MenuOption
          iconName="swap"
          text="Transfer prescriptions"
          onPress={() => {
            trackCategoryPress('transfer');
            setActiveActionSheet(<TransferActionSheet />);
          }}
          hide={isFeatureDisablingLoading || shouldDisableTransferPrescription}
        />
      ),
    },
  ];

  return isActionSheet ? (
    <ActionSheetV2
      title="How can we help?"
      analyticsName="assistant help"
    >
      <ListBase items={items} />
    </ActionSheetV2>
  ) : (
    <ListBase items={items} />
  );
};
